import { FC, useMemo } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { capitalCase } from 'change-case';
import { PortalRoutePath, baseEntityComparator, getPortalRoute } from '@models';
import { DateUtils, Tr } from '@utils';
import { useService } from '@hooks';
import { AdminError, NegativeCalculationData, adminErrorService } from '@services';
import { Box, Breadcrumbs, Typography } from '@mui/material';
import { DataTable, NoDataAnimation, RouterLinkAnchor } from '@components/common';
import { AdminErrorType } from '@services/admin-error/admin-error-type.enum';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';

type Entity = AdminError;

export interface IAdminDashboardDiagnosticsProps {}

export const AdminDashboardDiagnostics: FC<IAdminDashboardDiagnosticsProps> = () => {
	const { data: errors } = useService(() => adminErrorService.getAll());

	const columns = useMemo<ColumnDef<Entity>[]>(
		() => [
			{
				accessorKey: 'createdAt',
				enableSorting: false,
				size: 0.01,
				meta: { oneLine: true },
				header: () => <Tr.Portal path="admin-dashboard.created-on" />,
				cell: ({ row }) => DateUtils.format(row.original.createdAt)
			},
			{
				accessorKey: 'adminErrorType',
				enableSorting: false,
				size: 0.01,
				meta: { oneLine: true },
				header: () => <Tr.Portal path="admin-dashboard.error-type" />,
				cell: ({ row }) => capitalCase(row.original.adminErrorType)
			},
			{
				accessorKey: 'adminErrorPayload',
				enableSorting: true,
				header: () => <Tr.Portal path="admin-dashboard.error-details" />,
				cell: ({ row }) => {
					switch (row.original.adminErrorType) {
						case AdminErrorType.FUEL_SAVING_NEGATIVE: {
							const payload = row.original.adminErrorPayload as NegativeCalculationData;
							return (
								<Breadcrumbs separator={<HorizontalRuleIcon fontSize="small" color="disabled" />}>
									<RouterLinkAnchor href={`${getPortalRoute(PortalRoutePath.ORGANIZATIONS)}/${payload.organizationId}`}>
										<Typography variant="body2">
											<Tr.Portal path="admin-dashboard.organization" />
										</Typography>
									</RouterLinkAnchor>
									<RouterLinkAnchor href={`${getPortalRoute(PortalRoutePath.CAR_MODELS)}/${payload.mappedCarModelId}`}>
										<Typography variant="body2">
											<Tr.Portal path="admin-dashboard.mapped-car-model" />
										</Typography>
									</RouterLinkAnchor>
									<RouterLinkAnchor href={`${getPortalRoute(PortalRoutePath.CAR_MODELS)}/${payload.proposedCarModelId}`}>
										<Typography variant="body2">
											<Tr.Portal path="admin-dashboard.proposed-car-model" />
										</Typography>
									</RouterLinkAnchor>
									<Typography variant="body2" sx={{ color: 'common.black' }}>
										<strong>
											<Tr.Portal path="admin-dashboard.brand" />
										</strong>
										{`: ${payload.brand}`}
									</Typography>
									<Typography variant="body2" sx={{ color: 'common.black' }}>
										<strong>
											<Tr.Portal path="admin-dashboard.model" />
										</strong>
										{`: ${payload.model}`}
									</Typography>
									<Typography variant="body2" sx={{ color: 'common.black' }}>
										<strong>
											<Tr.Portal path="admin-dashboard.version" />
										</strong>
										{`: ${payload.version}`}
									</Typography>
									<Typography variant="body2" sx={{ color: 'common.black' }}>
										<strong>
											<Tr.Portal path="admin-dashboard.fleet-vehicle" />
										</strong>
										{`: ${payload.fleetVehicleId}`}
									</Typography>
								</Breadcrumbs>
							);
						}
						default: {
							return null;
						}
					}
				}
			}
		],
		[]
	);

	return Array.isArray(errors) && errors.length ? (
		<Box sx={{ flexGrow: 1, overflow: 'auto' }}>
			<DataTable
				columns={columns}
				data={errors}
				comparator={baseEntityComparator}
				emptyComponent={<NoDataAnimation message={<Tr.Portal path="admin-dashboard.no-error-message" />} />}
			/>
		</Box>
	) : null;
};
